import { useContext, useEffect, useRef, useState } from "react";
import Button from "../../components/ui/button/Button";
import styles from "./styles/Search.module.css";
import InputTextArea from "../../components/ui/input_text_area/InputTextArea";
import InputText from "../../components/ui/input_text/InputText";
import { t } from "i18next";
import InputDate from "../../components/ui/input_date/InputDate";
import InputPhone from "../../components/ui/input_phone/InputPhone";
import InputFileMulti from "../../components/ui/input_file_multi/InputFileMulti";
import DataContext from "../../context/DataContext";
import { axiosSW, axiosSW2 } from "../../api/axiosDefaults";
import { capitalizeFirstLetter } from "../../utils/utils";

const SALUTATION = ["Mr.", "Mrs.", "Mx."];

const DEFAULT_INPUTS = {
    reason: "",
    complaintDescription: "",

    documents: [],
    contactDetails: {
        salutation: SALUTATION[0],
        firstName: "",
        lastName: "",
        birthdate: "",
        email: "",
        phone: "",
    },
};

const Complaint = ({
    setShowComplaintForm,
    complaintStatus,
    setComplaintStatus,

    ticketNo,
    from_DateTime,
}) => {
    const [formInputs, setFormInputs] = useState(DEFAULT_INPUTS);
    const [hoverFlag, setHoverFlag] = useState(false); // State to manage hover flag
    const [disabelSubmitButton, setDisabelSubmitButton] = useState(false);
    const [complaintInProcess, setComplaintInProcess] = useState(false);
    const { handleError, connection } = useContext(DataContext);

    const firstInput = useRef(null);

    useEffect(() => {
        firstInput.current && firstInput.current.focus();
    }, []);

    useEffect(() => {
        setDisabelSubmitButton(validateInputs());
    }, [formInputs]);

    const handleChange = (event) => {
        setFormInputs((prev) => ({
            ...prev,
            [event.target.name]: event.target.value,
        }));
    };

    const handleChangeContact = (event) => {
        setFormInputs((prev) => ({
            ...prev,
            contactDetails: {
                ...prev.contactDetails,
                [event.target.name]: event.target.value,
            },
        }));
    };

    // remove the id key from all the objects in the documents array
    // this is needed to prepare payload for submitting
    const removeIdKey = (array) => {
        if (!Array.isArray(array) || !array?.length) return [];
        return array.map(({ id, ...rest }) => rest);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (disabelSubmitButton || complaintInProcess) return;
        const updatedContactDetails = complaintStatus
            ? null
            : formInputs?.contactDetails;

        const updatedDocuments = removeIdKey(formInputs?.documents); // remove the id keyfrom each object since it is not required for the endpoint
        const payLoad = {
            ...formInputs,
            uniqueIdentifier: connection?.uniqueId,
            ticketNo: ticketNo,
            fromDateTime: from_DateTime,
            complaintStatus: "new",
            documents: updatedDocuments,
            contactDetails: updatedContactDetails,
        };
        try {
            setComplaintInProcess(true);
            const { data } = await axiosSW2.post(
                "/tickets/submit-complaint",
                payLoad,
            );

            if (data?.statusOk) {
                setComplaintStatus({ text: "complaint filed", status: 0 });
                handleResetForm();
                setShowComplaintForm(false);
                setComplaintInProcess(false);
            } else {
                handleError(new Error("Submitting complaint error"));
                setComplaintInProcess(false);
            }
        } catch (error) {
            handleError(error);
            setComplaintInProcess(false);
        }
    };

    const handleResetForm = () => {
        setFormInputs(DEFAULT_INPUTS);
    };

    const handleCancelButton = () => {
        handleResetForm();
        setShowComplaintForm(false);
    };
    const emailValidation = () => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
            formInputs?.contactDetails?.email,
        );
    };
    const validateInputs = () => {
        if (complaintStatus) return validateDataInputsForAddFiles();
        return !(
            formInputs?.contactDetails?.salutation &&
            formInputs?.contactDetails?.firstName &&
            formInputs?.contactDetails?.lastName &&
            formInputs?.contactDetails?.birthdate &&
            formInputs?.contactDetails?.email &&
            emailValidation() &&
            formInputs?.contactDetails?.phone &&
            !validateDataInputs()
        );
    };
    const validateDataInputs = () => {
        return !(formInputs?.reason && formInputs?.complaintDescription);
    };
    const validateDataInputsForAddFiles = () => {
        return !(
            formInputs?.reason &&
            formInputs?.complaintDescription &&
            formInputs?.documents.length
        );
    };

    return (
        <div className={`${styles.main_complaint_container} v-flex-container`}>
            <h2 className={`${styles.main_complaint_header}`}>
                {t("complaint's data")}
            </h2>

            <form
                className={`v-flex-container`}
                onSubmit={handleSubmit}
                noValidate={true} // Disable default HTML5 validation
            >
                <div className={`${styles.main_data_container} flex-container`}>
                    {!complaintStatus ? (
                        <div
                            className={`${styles.right_data_container} v-flex-container`}
                        >
                            <InputText
                                ref={firstInput} // Reference for ticket number input
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`first name`)}
                                inputId="firstName"
                                inputValue={
                                    formInputs?.contactDetails?.firstName
                                }
                                handleChangeFunction={handleChangeContact}
                                required={
                                    hoverFlag &&
                                    !formInputs?.contactDetails?.firstName
                                } // Required validation
                            />
                            <InputText
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`last name`)}
                                inputId="lastName"
                                inputValue={
                                    formInputs?.contactDetails?.lastName
                                }
                                handleChangeFunction={handleChangeContact}
                                required={
                                    hoverFlag &&
                                    !formInputs?.contactDetails?.lastName
                                } // Required validation
                            />
                            <InputDate
                                label={t(`date of birth`)}
                                inputId={"birthdate"}
                                inputValue={
                                    formInputs?.contactDetails?.birthdate
                                }
                                handleChangeFunction={handleChangeContact}
                                required={
                                    hoverFlag &&
                                    !formInputs?.contactDetails?.birthdate
                                } // Required validation
                                toToday={true}
                            />
                            <InputPhone
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`phone number`)}
                                inputId="phone"
                                inputValue={formInputs?.contactDetails?.phone}
                                handleChangeFunction={handleChangeContact}
                                required={
                                    hoverFlag &&
                                    !formInputs?.contactDetails?.phone
                                } // Required validation
                            />
                            <InputText
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`email address`)}
                                inputId="email"
                                inputValue={formInputs?.contactDetails?.email}
                                handleChangeFunction={handleChangeContact}
                                required={hoverFlag && !emailValidation()} // Required validation
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    <div
                        className={`${styles.left_data_container} v-flex-container`}
                    >
                        <InputTextArea
                            label={t("reason")}
                            inputId={"reason"}
                            inputValue={formInputs?.reason}
                            handleChangeFunction={handleChange}
                            placeholder={capitalizeFirstLetter(
                                t("the complaint reason"),
                            )}
                            fullWidth={true}
                            required={hoverFlag && !formInputs?.reason} // Required validation
                        />
                        <InputTextArea
                            label={t("description")}
                            inputId={"complaintDescription"}
                            inputValue={formInputs?.complaintDescription}
                            handleChangeFunction={handleChange}
                            placeholder={capitalizeFirstLetter(
                                t("the complaint description"),
                            )}
                            fullWidth={true}
                            required={
                                hoverFlag && !formInputs?.complaintDescription
                            } // Required validation
                        />

                        <InputFileMulti
                            label={
                                t(`attachments`) +
                                ` ( ${formInputs?.documents?.length} - 3 )`
                            }
                            inputId={"documents"}
                            inputValue={formInputs?.documents}
                            handleChangeFunction={handleChange}
                            required={
                                hoverFlag && !formInputs?.documents.length
                            }
                        />
                    </div>
                </div>

                <div className={`${styles.buttons_container} flex-container`}>
                    <Button
                        body={t("submit")}
                        type={"submit"}
                        setHoverFlag={setHoverFlag}
                        disabled={disabelSubmitButton || complaintInProcess}
                    />
                    <Button
                        body={t("clear")}
                        type={"button"}
                        onClickFuction={handleResetForm}
                    />
                    <Button
                        body={t("cancel")}
                        type={"button"}
                        color={"red"}
                        onClickFuction={handleCancelButton}
                        disabled={complaintInProcess}
                        disableHTML={complaintInProcess}
                    />
                </div>
            </form>
        </div>
    );
};

export default Complaint;
